import { getInterpolatedValue } from 'features/timeSeries/utils/chartHelpers'
import { useCallback, useMemo, useState } from 'react'

import type {
  CapturePhase,
  DraftSelectionPreviewBounds,
  PendingCapture,
} from './mdDraftAlertCaseFlowTypes'
import { getPlotBounds } from './mdDraftAlertCaseFlowUtils'

type VisibleSensor = {
  id: string
}

type SensorAxisMapping = Record<string, unknown>

interface UseDraftAlertCaptureArgs {
  uPlotRef: React.MutableRefObject<import('uplot').default | null>
  chartAreaRef: React.MutableRefObject<HTMLDivElement | null>
  visibleSensors: readonly VisibleSensor[]
  visibleSensorIds: readonly string[]
  chartData: Parameters<typeof getInterpolatedValue>[0]
  sensorAxisMapping: SensorAxisMapping
  singleScaleMode: boolean
  timeRange: { from: string; to: string }
  canDraftAlerts: boolean
  onCaptureComplete: () => void
}

export function useDraftAlertCapture({
  uPlotRef,
  chartAreaRef,
  visibleSensors,
  visibleSensorIds,
  chartData,
  sensorAxisMapping,
  singleScaleMode,
  timeRange,
  canDraftAlerts,
  onCaptureComplete,
}: UseDraftAlertCaptureArgs) {
  const [capturePhase, setCapturePhase] = useState<CapturePhase>('idle')
  const [pendingCapture, setPendingCapture] = useState<PendingCapture | null>(
    null
  )
  const [dragSelection, setDragSelection] = useState<{
    startX: number
    currentX: number
  } | null>(null)
  const [capturePrimarySensorId, setCapturePrimarySensorId] =
    useState<string>('')

  const getPointerPosition = useCallback(
    (clientX: number, clientY: number) => {
      const uPlotInstance = uPlotRef.current
      const plotBounds = getPlotBounds(uPlotInstance, chartAreaRef.current)
      if (!uPlotInstance || !plotBounds) {
        return null
      }

      const overRect = uPlotInstance.over.getBoundingClientRect()
      return {
        x: clientX - overRect.left,
        y: clientY - overRect.top,
        plotBounds,
      }
    },
    [chartAreaRef, uPlotRef]
  )

  const getNearestSensorId = useCallback(
    (clientX: number, clientY: number) => {
      const pointer = getPointerPosition(clientX, clientY)
      const uPlotInstance = uPlotRef.current
      if (!pointer || !uPlotInstance) {
        return null
      }

      const xValue = uPlotInstance.posToVal(pointer.x, 'x')
      let bestSensorId: string | null = null
      let bestDistance = Number.POSITIVE_INFINITY

      visibleSensors.forEach((sensor) => {
        const yValue = getInterpolatedValue(chartData, sensor.id, xValue)
        if (yValue === null) {
          return
        }

        const scaleKey =
          singleScaleMode || !sensorAxisMapping[sensor.id] ? 'y' : sensor.id
        const yPos = uPlotInstance.valToPos(yValue, scaleKey, false)
        const distance = Math.abs(yPos - pointer.y)

        if (distance < bestDistance) {
          bestDistance = distance
          bestSensorId = sensor.id
        }
      })

      return bestSensorId
    },
    [
      chartData,
      getPointerPosition,
      sensorAxisMapping,
      singleScaleMode,
      uPlotRef,
      visibleSensors,
    ]
  )

  const getSelectionRange = useCallback(
    (startX: number, currentX: number) => {
      const uPlotInstance = uPlotRef.current
      const plotBounds = getPlotBounds(uPlotInstance, chartAreaRef.current)
      if (!uPlotInstance || !plotBounds) {
        return null
      }

      const clampedStartX = Math.min(
        plotBounds.right,
        Math.max(plotBounds.left, startX)
      )
      const clampedCurrentX = Math.min(
        plotBounds.right,
        Math.max(plotBounds.left, currentX)
      )
      const left = Math.min(clampedStartX, clampedCurrentX)
      const right = Math.max(clampedStartX, clampedCurrentX)
      const width = right - left
      if (width < 6) {
        return null
      }

      return {
        left,
        right,
        width,
        from: new Date(uPlotInstance.posToVal(left, 'x')).toISOString(),
        to: new Date(uPlotInstance.posToVal(right, 'x')).toISOString(),
      }
    },
    [chartAreaRef, uPlotRef]
  )

  const startCapture = useCallback(() => {
    if (!canDraftAlerts) {
      return
    }

    setCapturePhase('focus')
    setPendingCapture({
      primarySensorId: visibleSensorIds[0] ?? '',
      renderStartAt: timeRange.from,
      renderEndAt: timeRange.to,
      focusStartAt: timeRange.from,
      focusEndAt: timeRange.to,
    })
    setDragSelection(null)
    setCapturePrimarySensorId(visibleSensorIds[0] ?? '')
  }, [canDraftAlerts, timeRange.from, timeRange.to, visibleSensorIds])

  const cancelCapture = useCallback(() => {
    setCapturePhase('idle')
    setPendingCapture(null)
    setDragSelection(null)
    setCapturePrimarySensorId('')
  }, [])

  const resetCapture = useCallback(() => {
    setPendingCapture(null)
    setDragSelection(null)
    setCapturePrimarySensorId('')
    setCapturePhase('idle')
  }, [])

  const handleCaptureMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (capturePhase === 'idle') {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      const pointer = getPointerPosition(event.clientX, event.clientY)
      if (!pointer) {
        return
      }

      const nearestSensorId = getNearestSensorId(event.clientX, event.clientY)
      if (nearestSensorId) {
        setCapturePrimarySensorId(nearestSensorId)
      }

      setDragSelection({
        startX: pointer.x,
        currentX: pointer.x,
      })
    },
    [capturePhase, getNearestSensorId, getPointerPosition]
  )

  const handleCaptureMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (capturePhase === 'idle') {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      const nearestSensorId = getNearestSensorId(event.clientX, event.clientY)
      if (nearestSensorId) {
        setCapturePrimarySensorId(nearestSensorId)
      }

      if (!dragSelection) {
        return
      }

      const pointer = getPointerPosition(event.clientX, event.clientY)
      if (!pointer) {
        return
      }

      setDragSelection((currentSelection) =>
        currentSelection
          ? {
              ...currentSelection,
              currentX: pointer.x,
            }
          : currentSelection
      )
    },
    [capturePhase, dragSelection, getNearestSensorId, getPointerPosition]
  )

  const handleCaptureMouseUp = useCallback(
    (event?: React.MouseEvent<HTMLDivElement>) => {
      event?.preventDefault()
      event?.stopPropagation()

      if (!dragSelection) {
        return
      }

      const nextRange = getSelectionRange(
        dragSelection.startX,
        dragSelection.currentX
      )
      setDragSelection(null)

      if (!nextRange || capturePhase !== 'focus' || !pendingCapture) {
        return
      }

      setPendingCapture({
        ...pendingCapture,
        primarySensorId:
          capturePrimarySensorId || pendingCapture.primarySensorId,
        focusStartAt: nextRange.from,
        focusEndAt: nextRange.to,
      })
      setCapturePhase('idle')
      onCaptureComplete()
    },
    [
      capturePhase,
      capturePrimarySensorId,
      dragSelection,
      getSelectionRange,
      onCaptureComplete,
      pendingCapture,
    ]
  )

  const draftSelectionPreview: DraftSelectionPreviewBounds | null =
    useMemo(() => {
      if (!dragSelection) {
        return null
      }

      const selection = getSelectionRange(
        dragSelection.startX,
        dragSelection.currentX
      )
      const plotBounds = getPlotBounds(uPlotRef.current, chartAreaRef.current)
      if (!selection || !plotBounds) {
        return null
      }

      return {
        left: selection.left,
        width: selection.width,
        top: plotBounds.top,
        height: plotBounds.height,
      }
    }, [chartAreaRef, dragSelection, getSelectionRange, uPlotRef])

  return {
    capturePhase,
    pendingCapture,
    capturePrimarySensorId,
    draftSelectionPreview,
    startCapture,
    cancelCapture,
    resetCapture,
    handleCaptureMouseDown,
    handleCaptureMouseMove,
    handleCaptureMouseUp,
  }
}
